import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '../ui/button';

const isIOS = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
};

const isStandalone = () =>
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true);

const DISMISS_KEY = 'pwa-install-dismissed';

const InstallPrompt = () => {
    const [visible, setVisible] = useState(false);
    const [showIOSHelp, setShowIOSHelp] = useState(false);

    useEffect(() => {
        if (isStandalone()) return;
        const dismissedAt = localStorage.getItem(DISMISS_KEY);
        // Re-show after 7 days
        if (dismissedAt && Date.now() - Number(dismissedAt) < 7 * 24 * 60 * 60 * 1000) return;

        // Android/Desktop: wait for beforeinstallprompt
        const onAvailable = () => setVisible(true);
        window.addEventListener('pwa-install-available', onAvailable);
        if (window.__pwaInstallPrompt) setVisible(true);

        // iOS: show iOS instruction after small delay
        if (isIOS()) {
            const t = setTimeout(() => setVisible(true), 2000);
            return () => {
                clearTimeout(t);
                window.removeEventListener('pwa-install-available', onAvailable);
            };
        }

        return () => window.removeEventListener('pwa-install-available', onAvailable);
    }, []);

    const handleInstall = async () => {
        if (isIOS()) {
            setShowIOSHelp(true);
            return;
        }
        const prompt = window.__pwaInstallPrompt;
        if (!prompt) return;
        prompt.prompt();
        const choice = await prompt.userChoice;
        if (choice && choice.outcome) {
            window.__pwaInstallPrompt = null;
            setVisible(false);
        }
    };

    const handleDismiss = () => {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
        setVisible(false);
        setShowIOSHelp(false);
    };

    if (!visible) return null;

    if (showIOSHelp) {
        return (
            <div
                className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white dark:bg-zinc-900 border border-border rounded-md shadow-lg p-4 z-[1200] animate-in slide-in-from-bottom-5"
                data-testid="pwa-ios-help"
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Share className="text-primary" size={20} />
                        <h3 className="font-heading uppercase text-sm tracking-wider">Aggiungi alla Home</h3>
                    </div>
                    <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground" aria-label="Chiudi">
                        <X size={18} />
                    </button>
                </div>
                <ol className="text-sm text-muted-foreground space-y-2 mt-3 list-decimal list-inside">
                    <li>Tocca l'icona <b>Condividi</b> in basso (il quadrato con la freccia)</li>
                    <li>Scorri e scegli <b>"Aggiungi alla schermata Home"</b></li>
                    <li>Conferma con <b>"Aggiungi"</b> in alto a destra</li>
                </ol>
            </div>
        );
    }

    return (
        <div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-[#263238] text-white rounded-md shadow-lg p-4 z-[1200] animate-in slide-in-from-bottom-5"
            data-testid="pwa-install-prompt"
        >
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h3 className="font-heading uppercase text-sm tracking-wider mb-1">Installa Pezzaioli CRM</h3>
                    <p className="text-xs text-white/70">Accesso rapido dalla home, anche offline</p>
                </div>
                <button onClick={handleDismiss} className="text-white/60 hover:text-white" aria-label="Chiudi" data-testid="pwa-dismiss">
                    <X size={18} />
                </button>
            </div>
            <div className="flex gap-2 mt-3">
                <Button
                    onClick={handleInstall}
                    className="flex-1 btn-primary font-heading text-xs h-9"
                    data-testid="pwa-install-btn"
                >
                    <Download size={14} className="mr-2" />
                    INSTALLA
                </Button>
                <Button
                    variant="outline"
                    onClick={handleDismiss}
                    className="h-9 text-xs bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white"
                >
                    Dopo
                </Button>
            </div>
        </div>
    );
};

export default InstallPrompt;
